import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  PersonAddOutlined,
  EditOutlined,
  VisibilityOutlined,
} from '@mui/icons-material';
import { api } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';

interface SupplierFormProps {
  mode: 'create' | 'edit' | 'view';
}

const SupplierForm: React.FC<SupplierFormProps> = ({ mode }) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isViewMode = mode === 'view';
  const { alert } = useNotification();

  // Form state
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch supplier details in edit/view mode
  useEffect(() => {
    if ((mode === 'edit' || mode === 'view') && id) {
      const fetchSupplier = async () => {
        setFetching(true);
        try {
          const response = await api.get(`/api/partners/${id}`);
          const supplier = response.data.partner || response.data;
          setName(supplier.name || '');
        } catch (err) {
          console.error('Error fetching supplier:', err);
          setError('Failed to load supplier details');
        } finally {
          setFetching(false);
        }
      };
      fetchSupplier();
    }
  }, [mode, id]);

  const validateForm = (): boolean => {
    if (!name.trim()) {
      setError('Supplier name is required');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) return;

    setLoading(true);

    const payload = {
      type: 'SUPPLIER' as const,
      name: name.trim(),
    };

    try {
      if (mode === 'create') {
        const response = await api.post('/api/partners', payload);
        const supplierId = response.data?.id || response.data?.partner?.id;
        const supplierName = name.trim();
        // Reset form
        setName('');
        // Show success dialog
        await alert({
          title: 'Supplier Created',
          message: `${supplierName} (#${supplierId || ''}) created successfully!`,
          icon: 'success',
        });
      } else {
        await api.put(`/api/partners/${id}`, payload);
        navigate(-1);
      }
    } catch (err) {
      setError('Failed to save supplier');
      console.error('Error saving supplier:', err);
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 4, maxWidth: 500, mx: 'auto', mt: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        {mode === 'create' ? (
          <PersonAddOutlined sx={{ fontSize: 32, mr: 2, color: 'primary.main' }} />
        ) : mode === 'view' ? (
          <VisibilityOutlined sx={{ fontSize: 32, mr: 2, color: 'primary.main' }} />
        ) : (
          <EditOutlined sx={{ fontSize: 32, mr: 2, color: 'primary.main' }} />
        )}
        <Typography variant="h5" fontWeight="bold">
          {mode === 'create' ? 'Add New Supplier' : mode === 'view' ? 'Supplier Details' : 'Edit Supplier'}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        {/* Name */}
        <TextField
          fullWidth
          label="Supplier Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          sx={{ mb: 3 }}
          required
          placeholder="Enter supplier name"
          disabled={isViewMode}
          InputProps={{ readOnly: isViewMode }}
        />

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/suppliers')}
            disabled={loading}
          >
            {isViewMode ? 'Back' : 'Cancel'}
          </Button>
          {!isViewMode && (
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
            >
              {loading ? 'Saving...' : mode === 'create' ? 'Create Supplier' : 'Update Supplier'}
            </Button>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default SupplierForm;
